import React, { useCallback, useRef, useState } from "react";
import { Plus } from "lucide-react";
import { ConnectionLine } from "./connection-line";
import { CanvasNode } from "./canvas-node";
import { CanvasProps } from "../../../../pages/journey/types";

interface ExtendedCanvasProps extends CanvasProps {
  onToggleNodePalette?: () => void;
  isNodePaletteCollapsed?: boolean;
  onNodeClick?: (nodeId: string) => void; // Add this prop
}

export const Canvas: React.FC<ExtendedCanvasProps> = ({
  nodes,
  selectedNodeId,
  onSelectNode,
  onDropNode,
  onDragNode,
  onDeleteNode,
  onAddNode,
  onToggleNodePalette,
  isNodePaletteCollapsed = false,
  onNodeClick // Add this prop
}) => {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [hoveredConnectionIndex, setHoveredConnectionIndex] = useState<number | null>(null);
  const [pendingNodePosition, setPendingNodePosition] = useState<{ index: number, type: 'between' | 'end' } | null>(null);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const type = e.dataTransfer.getData("nodeType");
      if (type && canvasRef.current) {
        const rect = canvasRef.current.getBoundingClientRect();
        const x = 400;
        const y = e.clientY - rect.top - 24;

        onDropNode(type, x, y);
      }
    },
    [onDropNode]
  );

  const handleAddNodeAtConnection = useCallback((index: number) => {
    if (onToggleNodePalette) {
      if (isNodePaletteCollapsed) {
        setPendingNodePosition({ index, type: 'between' });
        onToggleNodePalette();
      } else {
        onAddNode('delay', nodes[index].id);
      }
    }
  }, [onToggleNodePalette, isNodePaletteCollapsed, onAddNode, nodes]);

  const handleAddNodeAtEnd = useCallback(() => {
    if (onToggleNodePalette) {
      if (isNodePaletteCollapsed) {
        setPendingNodePosition({ index: nodes.length - 1, type: 'end' });
        onToggleNodePalette();
      } else {
        onAddNode('delay');
      }
    }
  }, [onToggleNodePalette, isNodePaletteCollapsed, onAddNode, nodes]);

  const handleNodeTypeSelect = useCallback((type: string) => {
    if (pendingNodePosition) {
      if (pendingNodePosition.type === 'between') {
        onAddNode(type, nodes[pendingNodePosition.index].id);
      } else {
        onAddNode(type);
      }
      setPendingNodePosition(null);
    }
  }, [pendingNodePosition, onAddNode, nodes]);

  // Updated handleNodeClick function
  const handleNodeClick = (e: React.MouseEvent, nodeId: string) => {
    e.stopPropagation();

    // Call both functions - select the node AND trigger the modal
    if (onSelectNode) {
      onSelectNode(nodeId);
    }

    // This will trigger the modal opening
    onNodeClick?.(nodeId);
  };

  return (
    <div
      ref={canvasRef}
      className="flex-1 bg-gray-50 relative overflow-hidden"
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
    >
      {/* Background grid pattern */}
      <div className="absolute inset-0 bg-grid-gray-100 [mask-image:linear-gradient(0deg,transparent,black)]" />

      {/* Render connection lines between nodes */}
      {nodes.length > 1 &&
        nodes.map((node, index) => {
          if (index < nodes.length - 1) {
            const nextNode = nodes[index + 1];
            const centerX = node.position.x + 64;
            const connectionY = nextNode.position.y + 20;
            const isConnectionHovered = hoveredConnectionIndex === index;

            return (
              <div key={`connection-${node.id}-${nextNode.id}`} className="group">
                <ConnectionLine
                  from={node.position}
                  to={nextNode.position}
                />

                {/* Interactive plus button between nodes */}
                <button
                  className={`absolute transform -translate-x-1/2 -translate-y-1/2 z-10 transition-all duration-200 ${isConnectionHovered ? 'scale-110 opacity-100' : 'scale-100 opacity-0'
                    } group-hover:opacity-100`}
                  style={{
                    left: centerX,
                    top: connectionY - 10,
                  }}
                  onMouseEnter={() => setHoveredConnectionIndex(index)}
                  onMouseLeave={() => setHoveredConnectionIndex(null)}
                  onClick={() => handleAddNodeAtConnection(index)}
                  title="Add node here"
                >
                  <div className={`w-8 h-8 bg-white border-2 rounded-full flex items-center justify-center transition-colors shadow-lg ${isConnectionHovered ? 'border-teal-500 bg-teal-50' : 'border-gray-300 bg-white'
                    }`}>
                    <Plus className={`w-4 h-4 transition-colors ${isConnectionHovered ? 'text-teal-600' : 'text-gray-600'
                      }`} />
                  </div>
                </button>
              </div>
            );
          }
          return null;
        })}

      {/* Plus icon at the very end of the chain */}
      {nodes.length > 0 && (
        <>
          {/* Connection line from last node to end plus icon */}
          <svg
            className="absolute pointer-events-none z-0"
            style={{ top: 0, left: 0, width: "100%", height: "100%" }}
          >
            <line
              x1={nodes[nodes.length - 1].position.x + 64}
              y1={nodes[nodes.length - 1].position.y + 48}
              x2={nodes[nodes.length - 1].position.x + 64}
              y2={nodes[nodes.length - 1].position.y + 100}
              stroke="#9ca3af"
              strokeWidth={2}
              strokeDasharray="5,5"
            />

            {/* Connection dot at the end of the line */}
            <circle
              cx={nodes[nodes.length - 1].position.x + 64}
              cy={nodes[nodes.length - 1].position.y + 100}
              r="4"
              fill="#9ca3af"
            />
          </svg>

          {/* Interactive plus button at the end */}
          <button
            className={`absolute transform -translate-x-1/2 -translate-y-1/2 z-10 transition-all duration-200 ${hoveredConnectionIndex === nodes.length ? 'scale-110 opacity-100' : 'scale-100 opacity-100'
              }`}
            style={{
              left: nodes[nodes.length - 1].position.x + 64,
              top: nodes[nodes.length - 1].position.y + 120,
            }}
            onMouseEnter={() => setHoveredConnectionIndex(nodes.length)}
            onMouseLeave={() => setHoveredConnectionIndex(null)}
            onClick={handleAddNodeAtEnd}
            title="Add new node at end"
          >
            <div className={`w-8 h-8 bg-white border-2 rounded-full flex items-center justify-center transition-colors shadow-lg ${hoveredConnectionIndex === nodes.length ? 'border-teal-500 bg-teal-50' : 'border-gray-300 bg-white'
              }`}>
              <Plus className={`w-4 h-4 transition-colors ${hoveredConnectionIndex === nodes.length ? 'text-teal-600' : 'text-gray-600'
                }`} />
            </div>
          </button>
        </>
      )}

      {/* Updated nodes mapping with proper click handler */}
      {nodes.map((node) => (
        <CanvasNode
          key={node.id}
          node={node}
          isSelected={selectedNodeId === node.id}
          onClick={(e) => handleNodeClick(e, node.id)}
          onDragStart={onDragNode}
          onDelete={() => onDeleteNode(node.id)}
          onAddNode={(type) => onAddNode(type, node.id)}
        />
      ))}

      {nodes.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center text-gray-500">
          <div className="text-center bg-white p-8 rounded-lg border-2 border-dashed border-gray-300">
            <p className="text-lg mb-2 font-semibold">Start building your journey</p>
            <p className="text-sm text-gray-600">Drag nodes from the left panel to begin</p>
          </div>
        </div>
      )}
    </div>
  );
};